import { useTheme } from '@mui/material';

import { ServiceTaskResponse } from '@/api/data-contracts';

type ServiceType = ServiceTaskResponse['serviceType'];

const ALLOWED_STATUSES_BY_TYPE: Partial<Record<ServiceType, string[]>> = {
  PRINT: ['NEW', 'IN_REVIEW', 'PRINTING', 'COMPLETED', 'DELAYED', 'REJECTED'],
  DECORATION: ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'DELAYED', 'REJECTED'],
  NEUTRALIZATION: ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'DELAYED', 'REJECTED'],
};

export const ALL_EDITABLE_STATUSES = [
  'NEW',
  'PENDING',
  'IN_REVIEW',
  'PRINTING',
  'IN_PROGRESS',
  'COMPLETED',
  'REJECTED',
  'DELAYED',
];

export const ALL_DISPLAY_STATUSES = ALL_EDITABLE_STATUSES;

export const SERVICE_TASK_STATUS_LABEL_KEYS: Record<string, string> = {
  NEW: 'New',
  PENDING: 'pending',
  ON_HOLD: 'On hold',
  BLOCKED: 'Blocked',
  PRINT_PROOF_REQUIRED: 'Print proof required',
  IN_REVIEW: 'In review',
  PRINTING: 'Printing',
  IN_PROGRESS: 'In progress',
  CONFIRMED: 'Confirmed',
  IN_DELIVERY: 'In delivery',
  APPROVED: 'Approved',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  DELAYED: 'Delayed',
  CANCELLED: 'Cancelled',
};

export function useServiceTaskStatusColor(): (status: string) => string {
  const theme = useTheme();
  const colorsByStatus: Record<string, string> = {
    NEW: theme.palette.grey[500],
    PENDING: theme.palette.grey[500],
    IN_REVIEW: theme.palette.orange[500],
    DELAYED: theme.palette.orange[500],
    PRINTING: theme.palette.blue[500],
    IN_PROGRESS: theme.palette.blue[500],
    COMPLETED: theme.palette.green[500],
    REJECTED: theme.palette.red[500],
  };
  return (status: string) => colorsByStatus[status] ?? theme.palette.grey[500];
}

const SERVICE_TASK_PREDECESSOR: Partial<Record<ServiceType, ServiceType>> = {
  DECORATION: 'PRINT',
  NEUTRALIZATION: 'DECORATION',
};

export const SERVICE_TYPE_LABEL_KEYS: Partial<Record<ServiceType, string>> = {
  PRINT: 'Print',
  DECORATION: 'Decoration',
  NEUTRALIZATION: 'Neutralization',
};

export function getBlockingPredecessorType(
  task: Pick<ServiceTaskResponse, 'serviceType' | 'isPreviousTaskCompleted'>,
): ServiceType | undefined {
  const predecessorType = SERVICE_TASK_PREDECESSOR[task.serviceType];
  if (!predecessorType || task.isPreviousTaskCompleted) return undefined;
  return predecessorType;
}

export function getAllowedStatuses(serviceType: ServiceType | undefined): string[] {
  if (!serviceType) return ALL_EDITABLE_STATUSES;
  return ALLOWED_STATUSES_BY_TYPE[serviceType] ?? ALL_EDITABLE_STATUSES;
}

type CompletionPhotoSlots = Pick<ServiceTaskResponse, 'serviceType'> &
  Partial<
    Pick<
      ServiceTaskResponse,
      | 'beforeDecorationFiles'
      | 'afterDecorationFiles'
      | 'beforeNeutralizationFiles'
      | 'afterNeutralizationFiles'
    >
  >;

/**
 * Proof-photo precondition for the COMPLETED transition, mirroring the
 * backend's own so the UI never offers an action that is guaranteed to 400.
 *
 * - NEUTRALIZATION needs BOTH proof slots. Completing without the before-photos
 *   is rejected with `400 "Cannot complete NEUTRALIZATION service task: no
 *   before-neutralization proof photos have been uploaded"`. The gate used to
 *   check only the after-slot, so COMPLETED was enabled on a task the backend
 *   would always reject.
 * - DECORATION needs only the after-photos. This asymmetry is deliberate and
 *   verified end-to-end: DECORATION → COMPLETED is accepted with only
 *   after-decoration photos present, so it keeps the after-only rule.
 *
 * The before-slot is reported first because that is the condition the backend
 * checks first, so the tooltip names the slot that is actually blocking.
 */
export function getCompletionPhotoBlock(task: CompletionPhotoSlots): {
  blocked: boolean;
  tooltipKey?: string;
} {
  const count = (files?: { length: number }) => files?.length ?? 0;

  if (task.serviceType === 'DECORATION') {
    return count(task.afterDecorationFiles) > 0
      ? { blocked: false }
      : { blocked: true, tooltipKey: 'Upload monitor photos before marking the task as completed' };
  }

  if (task.serviceType === 'NEUTRALIZATION') {
    if (count(task.beforeNeutralizationFiles) === 0) {
      return {
        blocked: true,
        // TODO(i18n): extract once src/app/i18n/{en,ro}.json are free. i18next
        // returns the key when it is missing from the bundle, so EN reads
        // correctly today and RO falls back to EN until the key lands.
        tooltipKey: 'Upload before neutralization photos before marking the task as completed',
      };
    }
    return count(task.afterNeutralizationFiles) > 0
      ? { blocked: false }
      : {
          blocked: true,
          tooltipKey: 'Upload after neutralization photos before marking the task as completed',
        };
  }

  return { blocked: false };
}

export type MonitorPhotoCategory = 'Decoration' | 'Neutralization';
export type MonitorPhotoSlot = 'before' | 'after';

/**
 * The proof slot whose upload means "the crew is done on site", and therefore the
 * one that raises the "set to completed?" prompt: the after-photo of a decoration,
 * the before-photo of a neutralization (the shot of the still-decorated support).
 *
 * Single source of truth for every surface that can receive a monitor photo — the
 * grid cell, the task detail widget and the photo modal all decide whether to
 * prompt from this, so they can never drift apart.
 */
export function getStatusPromptSlot(category: MonitorPhotoCategory): MonitorPhotoSlot {
  return category === 'Decoration' ? 'after' : 'before';
}

export type ArtworkApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export function getArtworkPreviewBlock(
  serviceType: ServiceType | undefined,
  artworkStatus: ArtworkApprovalStatus | undefined,
  previewCount: number,
): { blocked: boolean; tooltipKey?: string } {
  if (serviceType !== 'PRINT' || artworkStatus === 'APPROVED') return { blocked: false };
  if (artworkStatus === 'REJECTED') {
    return { blocked: true, tooltipKey: 'artwork_preview_rejected' };
  }
  return {
    blocked: true,
    tooltipKey:
      previewCount > 0 ? 'artwork_preview_not_accepted' : 'upload_artwork_preview_to_continue',
  };
}

export function getPrintPlanningArtworkBlock(
  serviceType: ServiceType | undefined,
  artworkUploaded: boolean | undefined,
  artworkStatus: ArtworkApprovalStatus | undefined,
): { blocked: boolean; tooltipKey?: string } {
  if (serviceType !== 'PRINT' || artworkUploaded || artworkStatus) return { blocked: false };
  return { blocked: true, tooltipKey: 'print_planning_requires_artwork' };
}
